// Central nav config used by Rail, SubNav, and route-to-section resolution.
// Each top-level section has a base path and (optionally) sub-pages.

export const SECTIONS = [
  { key: 'dash',    icon: 'dash',   label: 'Dashboard',          base: '/',               roles: ['admin','bm','sales','rep'] },
  { key: 'msg',     icon: 'msg',    label: 'Messenger',          base: '/messenger',      roles: ['admin','bm','sales','rep'] },
  { key: 'reports', icon: 'report', label: 'Reports',            base: '/reports',        roles: ['admin','bm','sales'] },
  { key: 'vcp',     icon: 'vcp',    label: 'My VCP',             base: '/my-vcp',         roles: ['admin','bm','sales','rep'] },
  { key: 'cust',    icon: 'cust',   label: 'Customers',          base: '/customers',      roles: ['admin','bm','sales','rep'] },
  { key: 'bdm',     icon: 'visit',  label: 'BDM Manager',        base: '/bdm',            roles: ['admin','rep'] },
  // Sales operators' read-mostly window onto the BDM flow (B-030). Their
  // requests, resulting visits, feedback captured, follow-ups arising. Admin
  // rep already have a full BDM section — this section is sales-only so
  // they get a first-class icon and don't hit the RoleGate wall on /bdm.
  { key: 'bdmact',  icon: 'flag',   label: 'BDM Activity',       base: '/bdm/my-activity',roles: ['sales'] },
  { key: 'calls',   icon: 'phone',  label: 'Calls',              base: '/calls',          roles: ['admin','bm'] },
  { key: 'hols',    icon: 'cal',    label: 'My Holidays',        base: '/myholidays',     roles: ['admin','bm','sales','rep','fleet'] },
  { key: 'tma',     icon: 'tma',    label: 'Time Mgmt',          base: '/tma',            roles: ['admin','bm','sales','rep','fleet'] },
  { key: 'fleet',   icon: 'fleet',  label: 'Fleet',              base: '/fleet',          roles: ['admin','bm','fleet'] },
  { key: 'wksp',    icon: 'wrench', label: 'Workshop Essentials',base: '/workshop',       roles: ['admin','bm','rep'] },
];

export const SECTIONS_BOTTOM = [
  { key: 'notif',    icon: 'flag',   label: 'Notifications',  base: '/notifications',                roles: ['admin','bm','sales','rep','fleet'] },
  { key: 'drift',    icon: 'slider', label: 'Customer Drift', base: '/control-panel/customer-drift', roles: ['admin','bm'] },
  { key: 'visitpln', icon: 'cal',    label: 'Visit Plans',    base: '/control-panel/visit-plans',    roles: ['admin','rep'] },
  { key: 'users',    icon: 'users',  label: 'User Management', base: '/users',                       roles: ['admin'] },
  { key: 'cog',      icon: 'cog',    label: 'Settings',       base: '/settings',                     roles: ['admin','bm'] },
];

// Sub-pages per section. Each sub has slug (url segment), label, and optional roles.
export const SUBS = {
  users: [
    { slug: '',       label: 'Directory' },
    { slug: 'logins', label: 'Logins', roles: ['admin'] },
  ],
  // Reports has no flat tab strip — navigation flows through the hub at
  // /reports (see src/pages/reports/reportsManifest.js). SubNav renders a
  // breadcrumb back to the hub instead.
  vcp: [
    { slug: '',           label: 'My VCP' },
    { slug: 'management', label: 'VCP management', roles: ['admin','bm'] },
  ],
  bdm: [
    { slug: '',              label: 'Dashboard' },
    { slug: 'visits',        label: 'Visits' },
    { slug: 'visit-search',  label: 'Search' },
    { slug: 'visit-requests', label: 'Requests' },
    { slug: 'follow-ups',    label: 'Follow-ups' },
    { slug: 'feedback',      label: 'Feedback' },
    // Rep visit log retired — past visits now log through the unified + Visit
    // form (past date → auto-complete with notes as feedback). Route still
    // mounted so deep links don't 404.
    // Admin-only — sub-tab disappears for BDMs (they don't need to triage
    // the whole team); their own data is on the dashboard / search tabs.
    { slug: 'oversight',     label: 'Oversight', roles: ['admin'] },
  ],
  calls: [
    { slug: '',            label: 'Overview' },
    { slug: 'customers',   label: 'Customers' },
    { slug: 'staff',       label: 'Staff' },
    { slug: 'extensions',  label: 'Extensions' },
  ],
  tma: [
    { slug: 'calendar', label: 'Calendar' },
    { slug: 'holidays', label: 'Holidays' },
    { slug: 'shifts',   label: 'Shifts' },
  ],
  fleet: [
    { slug: '',           label: 'Vans per site' },
    { slug: 'vehicles',   label: 'Vehicles' },
    { slug: 'work-items', label: 'Work items' },
    { slug: 'repairs',    label: 'Repairs' },
    { slug: 'penalties',  label: 'Penalties' },
    { slug: 'drivers',    label: 'Drivers' },
  ],
  drift: [
    { slug: '',       label: 'Rules' },
    { slug: 'alerts', label: 'Alerts' },
    { slug: 'report', label: 'Report' },
  ],
  settings: [
    { slug: '',            label: 'Settings home' },
    { slug: 'staff',       label: 'Staff' },
    { slug: 'branch',      label: 'Branches' },
    { slug: 'assigncust',  label: 'Customer salespeople' },
    { slug: 'messages',    label: 'Messages' },
    { slug: 'shifts',      label: 'Shifts' },
    { slug: 'holidays',      label: 'Holidays policy' },
    { slug: 'notifications', label: 'Notifications' },
    { slug: 'wallboards',    label: 'Wallboards' },
    { slug: 'about',         label: 'About' },
    { slug: 'whats-new',     label: "What's new" },
  ],
};

// Resolve a pathname to { section, sub } — used to highlight nav.
// Matches the longest section base that is a prefix of pathname, so
// multi-segment bases (e.g. /control-panel/customer-drift) work alongside
// single-segment ones (e.g. /bdm).
export const resolveRoute = (pathname) => {
  if (pathname === '/' || pathname === '') return { section: 'dash', sub: '' };
  const clean = '/' + pathname.replace(/^\/+|\/+$/g, '');
  const all = [...SECTIONS, ...SECTIONS_BOTTOM]
    .slice()
    .sort((a, b) => b.base.length - a.base.length);
  const match = all.find(s => s.base !== '/' && (clean === s.base || clean.startsWith(s.base + '/')));
  if (!match) return { section: null, sub: '' };
  const tail = clean.slice(match.base.length).replace(/^\/+/, '');
  const sub = tail.split('/')[0] || '';
  return { section: match.key, sub };
};

// Given a section key, return its base path for navigation.
export const sectionBase = (key) => {
  const all = [...SECTIONS, ...SECTIONS_BOTTOM];
  return all.find(s => s.key === key)?.base || '/';
};

// Role-filter helper.
export const canAccess = (roles, status) => {
  if (!roles || roles.length === 0) return true;
  if (!status) return false;
  return roles.includes(status);
};
